import React, { useState } from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';

const DISMISS_KEY = 'cerebro_viral_banner_dismissed';

interface ViralWelcomeBannerProps {
  onCtaClick: () => void;
}

export const ViralWelcomeBanner: React.FC<ViralWelcomeBannerProps> = ({ onCtaClick }) => {
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      const isViral =
        params.get('utm_source') === 'user_signature' ||
        params.get('ref') === 'signature';
      const isDismissed = sessionStorage.getItem(DISMISS_KEY) === 'true';
      return isViral && !isDismissed;
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    setIsVisible(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {}
  };

  if (!isVisible) return null;

  return (
    <aside
      aria-label="Baner powitalny z podpisu email"
      style={{
        width: '100%',
        background: 'linear-gradient(90deg, #090d16 0%, #0d1a2d 50%, #090d16 100%)',
        borderBottom: '1px solid rgba(56, 189, 248, 0.3)',
        boxShadow: '0 4px 24px rgba(2, 132, 199, 0.18)',
        position: 'relative',
        zIndex: 60,
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        {/* Left message & Badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: '1 1 500px',
            minWidth: 260,
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px',
              borderRadius: 9999,
              background: 'rgba(56, 189, 248, 0.15)',
              border: '1px solid rgba(56, 189, 248, 0.35)',
              color: '#38bdf8',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              flexShrink: 0,
            }}
          >
            <Sparkles size={13} style={{ color: '#38bdf8' }} />
            <span>CerebroSign</span>
          </div>

          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              fontWeight: 500,
              color: '#f1f5f9',
              lineHeight: 1.45,
            }}
          >
            Podoba Ci się podpis w mailu od Twojego kontrahenta?{' '}
            <span style={{ color: '#38bdf8', fontWeight: 600 }}>
              Wygeneruj taki sam dla siebie lub całego zespołu w 30 sekund!
            </span>
          </p>
        </div>

        {/* Right actions: CTA button + Dismiss button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onCtaClick}
            type="button"
            className="btn btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 18px',
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 2px 12px rgba(2, 132, 199, 0.4)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(2, 132, 199, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(2, 132, 199, 0.4)';
            }}
          >
            <span>Stwórz darmowy podpis</span>
            <ArrowRight size={14} />
          </button>

          <button
            onClick={handleDismiss}
            type="button"
            aria-label="Zamknij powiadomienie"
            title="Zamknij"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.15s ease, background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#f8fafc';
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#94a3b8';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};
